import { Injectable } from '@angular/core';
declare var THREE: any;
import { StageService } from './stage.service';
import { SceneUtilsService } from './scene-utils.service';
import { BuildFloorService } from './build-floor.service';
import { Select3dService } from './select-3d.service';
import { RailingGenerationService } from './railing/services/railing-generation.service';
import { EventLogService, Actions } from './eventLog';
import { BuildingCheckerService } from './building-checker.service';
import { ToasterService } from 'angular2-toaster';
import { InteractiveSpriteComponentBase } from '../interactive-sprite-component-base';
import { ConstantsService } from './constants.service';
import {AutoScaleSprite} from '../custom/AutoScaleSprite';
import { WallBuilderService } from './wall/wall-builder.service';

@Injectable()
export class BuildTierService extends InteractiveSpriteComponentBase {

  private sprites = [];
  private loader = new THREE.TextureLoader();
  tierType: string;
  private raycaster: any = new THREE.Raycaster();
  private selectTierSubscription: any;

  constructor(
    private stage: StageService,
    private sceneUtils: SceneUtilsService,
    private buildFloorService: BuildFloorService,
    private select3dService: Select3dService,
    private railingGeneration: RailingGenerationService,
    private eventLog: EventLogService,
    private buildingCheckerService: BuildingCheckerService,
    private toasterService: ToasterService,
    private constants: ConstantsService,
    private wallBuilder: WallBuilderService
  ) {
    super();
    this.disable();
  }

  drawSprites(tierType: string): void {

    const tierConfig = this.constants.tierTypeSettingsMap[tierType];
    this.tierType = tierType;
    const spriteUrl = tierConfig.spriteUrl;

    const floor = this.select3dService.getSelectedTier();
    const points = floor.userData.points;
    const count = points.length;
    const currentTierheight = floor.getWorldPosition(new THREE.Vector3()).y + 5;

    if (this.buildingCheckerService.checkHeightForTier(tierType, floor.getWorldPosition(new THREE.Vector3()).y)) {

      for (let i = 0; i < points.length; i++) {
        if (!points[i].superstructureType) {
          const spriteMap = this.loader.load(spriteUrl);

          const spriteMaterial = new THREE.SpriteMaterial({ map: spriteMap, color: 0xffffff, depthTest: false, depthWrite: false });

          const sprite = new AutoScaleSprite(50, 50, spriteMaterial);

          const edge = {
            firstPoint: points[i],
            secondPoint: points[(i + 1) % count],
            length: Math.sqrt(
              Math.pow((points[i].x - points[(i + 1) % count].x), 2) +
              Math.pow((points[i].y - points[(i + 1) % count].y), 2)
            )
          };

          sprite.userData.edge = edge;
          sprite.userData.height = currentTierheight - 5 + tierConfig.tierHeightShift;
          sprite.userData.floorId = floor.id;
          sprite.userData.superstructureType = tierType;
          sprite.userData.distanceToTier = tierConfig.distanceToTier;

          this.sprites.push(sprite);
          sprite.position.set(
            (points[i].x + points[(i + 1) % points.length].x) / 2,
            currentTierheight,
            (points[i].y + points[(i + 1) % points.length].y) / 2
          );
          this.stage.add(sprite);
        }
      }
    }
    else {
      this.showToast('warning', 'Warning', this.constants.tierTypeSettingsMap[tierType].errorMessage);
      this.disable();
    }
  }

  mouseDown(e: MouseEvent) {
    const p = this.sceneUtils.getRay(e.offsetX, e.offsetY);
    this.raycaster.ray.origin.copy(p.ray.origin);
    this.raycaster.ray.direction.copy(p.ray.direction);

    const intersection = this.raycaster.intersectObjects(this.sprites);
    if (intersection.length) {
      const sprite = intersection[0].object;
      const config = {
        height: sprite.userData.height,
        edge: sprite.userData.edge,
        floorId: sprite.userData.floorId,
        superstructureType: sprite.userData.superstructureType,
        distanceToTier: sprite.userData.distanceToTier
      };
      this.buildTier(config);
      this.removeSprites();
    }
  }

  buildTier(config: any): void {

    const tierHeight = config.height;
    const edge = config.edge;
    const pointsForTier = this.getTierPoints(edge, config);
    const floor = this.stage.getFloorById(config.floorId);
    floor.userData.isOuterTier = false;

    const mesh = this.buildFloorService.buildFloor({ points: pointsForTier, height: tierHeight, parentCustomId: floor.userData.customId });

    edge.firstPoint.childCustomId = mesh.userData.customId;
    edge.firstPoint.superstructureType = config.superstructureType;
    edge.firstPoint.tierId = mesh.id; // refactor

    mesh.userData.edge = edge;

    mesh.userData.point = mesh.userData.points[3];
    mesh.userData.points[3].edge = mesh.userData.edges[3];

    this.railingGeneration.buildRailing(this.railingGeneration.IsVisible());

    this.eventLog.dispatch(new Actions.AddTierAction(mesh));

    this.rebuildWalls();
  }

  rebuildWalls() {
    const walls = this.stage.getWalls();

    if (walls && walls.length) {
      walls.forEach(w => {
        this.wallBuilder.createWall(w.userData.config);
      });
    }
  }

  removeSprites(): void {
    this.stage.removeMany(this.sprites);
    this.sprites = [];
  }

  private getTierPoints(edge, config): Array<any> {

    const A = new THREE.Vector3(edge.firstPoint.x, 0, edge.firstPoint.y);
    const B = new THREE.Vector3(edge.secondPoint.x, 0, edge.secondPoint.y);

    const AB = new THREE.Vector3(B.x - A.x, 0, B.z - A.z);
    const vect = new THREE.Vector3(AB.z, 0, -AB.x);
    const norm = vect.normalize();

    const nearPointsDistance = norm.clone().setLength(config.distanceToTier),
      farPointsDistance = norm.clone().setLength(182.4); // to do make value descriptive

    const firstNearPoint = A.clone().add(nearPointsDistance),
      firstFarPoint = A.clone().add(farPointsDistance),
      secondFarPoint = B.clone().add(farPointsDistance),
      secondNearPoint = B.clone().add(nearPointsDistance),
      width = AB.length() - this.constants.minimalSpaceForStairsOnSIde*2;

    const points = [
      {
        x: firstNearPoint.x,
        y: firstNearPoint.z
      },
      {
        x: firstFarPoint.x,
        y: firstFarPoint.z
      },
      {
        x: secondFarPoint.x,
        y: secondFarPoint.z
      },
      {
        x: secondNearPoint.x,
        y: secondNearPoint.z,
        superstructureType: config.superstructureType,
        width: width,
        distance: AB.length() / 2,
        parent: true // to do rename property
      },
    ]

    return points;
  }

  showToast(type: string, title: string, body: string): void {
    this.toasterService.popAsync(type, title, body);
  }

  disable() {
    this.removeSprites();
    this.selectTierSubscription && this.selectTierSubscription.unsubscribe();
    super.disable();
  }

  enable() {
    super.enable();
    if (!this.selectTierSubscription || this.selectTierSubscription.isStopped) {
      this.selectTierSubscription = this.select3dService.selectedTierSubject.subscribe(value => {
        if (this.isEnabled()) {
          this.removeSprites();
          this.drawSprites(this.tierType);
        }
      });
    }
  }

}



// WEBPACK FOOTER //
// ./src/app/services/build-tier.service.ts